import { Component, computed, effect, inject, Input, input, output, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { CustomUdtViewModel, CustomPoint } from 'src/app/wellbore-inputs/models/undisturbed-temperature.model';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { UnitsGridColumn, UnitsGridPasteTransform } from 'src/app/shared/components/units-grid-input/units-grid-input.models';

/**
 * Custom Points UDT Component (V2)
 * 
 * Uses ngx-sub-form for automatic ControlValueAccessor - matches prototype pattern
 * 
 * Usage:
 * <app-udt-custom-points-v2 
 *   formControlName="customPoints"
 *   [viewModel]="customUdtViewModel()" />
 */
@Component({
    selector: 'app-udt-custom-points-v2',
    templateUrl: './udt-custom-points-v2.component.html',
    styles: [],
    providers: subformComponentProviders(UdtCustomPointsV2Component),
    standalone: false
})
export class UdtCustomPointsV2Component {
    private fb = inject(FormBuilder);
    private lastBoundSignature = '';

    private readonly viewModelSignal = signal<CustomUdtViewModel | null>(null);

    @Input()
    set viewModel(value: CustomUdtViewModel | null) {
        this.viewModelSignal.set(value);
    }

    get viewModel(): CustomUdtViewModel | null {
        return this.viewModelSignal();
    }

    // Output signal - emitted when user clicks reset button
    resetClicked = output<void>();

    readonly userUnits = input<UserUnitsModel | null>(null);

    // Keep UDT-specific behavior in this feature component. The shared grid stays generic.
    readonly columns = computed<readonly UnitsGridColumn[]>(() => [
        {
            kind: 'number',
            header: 'TVD',
            field: 'trueVerticalDepth',
            unitKey: 'longLengths'
        },
        {
            kind: 'number',
            header: 'Temperature',
            field: 'temperature',
            unitKey: 'temperature',
            minFractionDigits: 1,
            maxFractionDigits: 2,
            formatFractionDigits: 1
        }
    ]);

    // ngx-sub-form handles ControlValueAccessor automatically
    form = createForm<CustomUdtViewModel['data']>(this, {
        formType: FormType.SUB,
        formControls: {
            customPoints: this.fb.array<FormGroup>([])
        }
    });

    constructor() {
        // Extract data from viewModel for ng-table-grid (pattern from prototype)
        effect(() => {
            const vm = this.viewModelSignal();
            const customPoints: CustomPoint[] = vm?.data?.customPoints ?? [];
            if (!vm) {
                return;
            }

            const incomingSignature = this.normalizeCustomPoints(customPoints);
            if (incomingSignature === this.lastBoundSignature) {
                this.applyUpdatedValidators(vm);
                return;
            }

            const pointsArray = this.getPointsArray();
            pointsArray.clear();
            customPoints.forEach((point: CustomPoint) => {
                pointsArray.push(this.createPointFormGroup(point, vm));
            });
            this.ensureTrailingInputRow();
            this.applyDepthOrderingErrors();
            this.lastBoundSignature = incomingSignature;
        });
    }

    private rowHasValues(group: FormGroup): boolean {
        const depth = group.get('trueVerticalDepth')?.value;
        const temperature = group.get('temperature')?.value;
        return depth !== null
            && depth !== undefined
            && depth !== ''
            && temperature !== null
            && temperature !== undefined
            && temperature !== '';
    }

    private applyDepthOrderingErrors(): void {
        const pointsArray = this.getPointsArray();
        let previousDepth: number | null = null;

        pointsArray.controls.forEach((group: FormGroup) => {
            const depthControl = group.get('trueVerticalDepth');
            if (!depthControl) {
                return;
            }

            const currentValue = depthControl.value;
            const hasDepthValue = currentValue !== null && currentValue !== undefined && currentValue !== '';

            const mergedErrors: Record<string, unknown> = { ...(depthControl.errors ?? {}) };
            delete mergedErrors.depthOrder;

            if (hasDepthValue && previousDepth !== null && Number(currentValue) <= previousDepth) {
                mergedErrors.depthOrder = 'Depth must be greater than the previous depth.';
            }

            depthControl.setErrors(Object.keys(mergedErrors).length > 0 ? mergedErrors : null);

            if (hasDepthValue) {
                previousDepth = Number(currentValue);
            }
        });
    }

    private ensureTrailingInputRow(): void {
        const pointsArray = this.getPointsArray();

        if (pointsArray.length === 0) {
            pointsArray.push(this.createNewPointFormGroup());
            return;
        }

        while (
            pointsArray.length > 1
            && !this.rowHasValues(pointsArray.at(pointsArray.length - 1) as FormGroup)
            && !this.rowHasValues(pointsArray.at(pointsArray.length - 2) as FormGroup)
        ) {
            pointsArray.removeAt(pointsArray.length - 1);
        }

        const lastRow = pointsArray.at(pointsArray.length - 1) as FormGroup;
        if (this.rowHasValues(lastRow)) {
            pointsArray.push(this.createNewPointFormGroup());
        }
    }

    private normalizeCustomPoints(customPoints: CustomPoint[]): string {
        return JSON.stringify((customPoints ?? []).map(point => ({
            trueVerticalDepth: point?.trueVerticalDepth,
            temperature: point?.temperature
        })));
    }

    private applyUpdatedValidators(vm: CustomUdtViewModel): void {
        const pointsArray = this.getPointsArray();
        pointsArray.controls.forEach((group: FormGroup) => {
            group.get('trueVerticalDepth')?.setValidators([
                Validators.required,
                Validators.min(vm.validation.trueVerticalDepth.min ?? 0),
                Validators.max(vm.validation.trueVerticalDepth.max ?? 50000)
            ]);
            group.get('temperature')?.setValidators([
                Validators.required,
                Validators.min(vm.validation.temperature.min ?? -500),
                Validators.max(vm.validation.temperature.max ?? 1000)
            ]);
            group.get('trueVerticalDepth')?.updateValueAndValidity({ emitEvent: false });
            group.get('temperature')?.updateValueAndValidity({ emitEvent: false });
        });

        this.applyDepthOrderingErrors();
    }

    getPointsArray(): FormArray<FormGroup> {
        return this.form.formGroup.get('customPoints') as FormArray<FormGroup>;
    }

    createPointFormGroup(point: CustomPoint, vm: CustomUdtViewModel): FormGroup {
        return this.fb.group({
            trueVerticalDepth: new FormControl(point.trueVerticalDepth, [
                Validators.required,
                Validators.min(vm.validation.trueVerticalDepth.min ?? 0),
                Validators.max(vm.validation.trueVerticalDepth.max ?? 50000)
            ]),
            temperature: new FormControl(point.temperature, [
                Validators.required,
                Validators.min(vm.validation.temperature.min ?? -500),
                Validators.max(vm.validation.temperature.max ?? 1000)
            ])
        });
    }

    private createNewPointFormGroup(): FormGroup {
        const vm = this.viewModelSignal();
        if (!vm) {
            return new FormGroup({
                trueVerticalDepth: new FormControl(null, [Validators.required]),
                temperature: new FormControl(null, [Validators.required])
            });
        }
        return new FormGroup({
            trueVerticalDepth: new FormControl(null, [
                Validators.required,
                Validators.min(vm.validation.trueVerticalDepth.min ?? 0),
                Validators.max(vm.validation.trueVerticalDepth.max ?? 50000)
            ]),
            temperature: new FormControl(null, [
                Validators.required,
                Validators.min(vm.validation.temperature.min ?? -500),
                Validators.max(vm.validation.temperature.max ?? 1000)
            ])
        });
    }

    // Row factory passed to the generic grid so it can grow rows on matrix paste.
    public createPointRow = (): FormGroup => this.createNewPointFormGroup();

    // UDT-specific guard: if the pasted block starts with the same TVD as the row
    // directly above the paste target, drop that first pasted row to avoid duplicates.
    public readonly customPointsPasteTransform: UnitsGridPasteTransform = ({
        parsedRows,
        startRowIndex,
        startColumnIndex,
        columns
    }) => {
        if (!parsedRows.length) {
            return parsedRows;
        }

        const depthColumn = columns[startColumnIndex];
        const firstDepth = Number(parsedRows[0]?.[0]);
        const previousDepthValue = this.getPointsArray().at(startRowIndex - 1)?.get('trueVerticalDepth')?.value;
        const previousDepth = Number(previousDepthValue);
        if (
            startRowIndex > 0
            && depthColumn?.field === 'trueVerticalDepth'
            && Number.isFinite(firstDepth)
            && Number.isFinite(previousDepth)
            && firstDepth === previousDepth
        ) {
            return parsedRows.slice(1);
        }

        return parsedRows;
    };

    addPoint(): void {
        const pointsArray = this.getPointsArray();
        pointsArray.push(this.createNewPointFormGroup());
        this.ensureTrailingInputRow();
        this.applyDepthOrderingErrors();
        this.form.formGroup.markAsDirty();
        this.form.formGroup.updateValueAndValidity();
    }

    removePoint(index: number): void {
        const pointsArray = this.getPointsArray();
        if (index < 0 || index >= pointsArray.length) {
            return;
        }

        pointsArray.removeAt(index);
        this.ensureTrailingInputRow();
        this.applyDepthOrderingErrors();
        this.form.formGroup.markAsDirty();
        this.form.formGroup.updateValueAndValidity();
    }

    onCellEdited(): void {
        this.ensureTrailingInputRow();
        this.applyDepthOrderingErrors();
        this.form.formGroup.markAsDirty();
        this.form.formGroup.updateValueAndValidity();
    }

    requestResetCustomPoints(): void {
        this.resetClicked.emit();
    }
}
